import {
  Button,
  FilterLabels,
  GenericFilter,
  SelectAll,
} from '@energybox/react-ui-library/dist/components';
import {
  EquipmentGroup,
  EquipmentType,
} from '@energybox/react-ui-library/dist/types';
import { classNames } from '@energybox/react-ui-library/dist/utils';
import equals from 'ramda/src/equals';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import {
  useEquipmentTypes,
  useEquipmentTypesById,
} from '../../../../hooks/useEquipmentTypes';
import usePrevious from '../../../../hooks/usePrevious';
import EquipmentMenuItem, {
  ListItem as EquipmentMenuListItem,
} from '../../FilterItems/EquipmentMenuItem';
import FilterMenuContent from '../../FilterMenuContent';
import FilterMenuFooter from '../../FilterMenuFooter';
import { sortByTitleWithEmptyStringToLast } from '../../../../utils/sorting';
import styles from './EquipmentMenuFilter.module.css';
import useDynamicFilter from '../../../../hooks/useDynamicFilter';

type Props = {
  className?: string;
  dropDownWrapperClassName?: string;
  label: string;
  isInDropdown?: boolean;
  relevantItemTypeIds?: number[];
  relevantItemGroupId?: number;
  disabled?: boolean;
  onChange?: (selectedSiteIds: number[]) => void;
  setFilter?: (updatedSiteIds: number[]) => void;
  activeFilters?: number[];
  isOnlyHvacType?: boolean;
};

const EquipmentMenuFilter: React.FC<Props> = ({
  className,
  dropDownWrapperClassName,
  label,
  isInDropdown,
  relevantItemTypeIds,
  relevantItemGroupId,
  disabled,
  onChange,
  isOnlyHvacType,
}: Props) => {
  const {
    selectedFilters: equipmentTypeFilters,
    setFilter: setEquipmentFilter,
  } = useDynamicFilter<number>('eqType', (value) => parseInt(value));
  const allEquipmentTypes = useEquipmentTypes();
  const controllableHvacTypes = allEquipmentTypes.filter((equipment) => {
    return equipment.controllable;
  });
  const equipmentTypes = isOnlyHvacType
    ? controllableHvacTypes
    : allEquipmentTypes;
  const equipmentTypesById = useEquipmentTypesById();
  const equipmentTypeIds = equipmentTypes.map(({ id }) => id);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [groups, setGroups] = useState<Map<string, EquipmentGroup>>(new Map());
  const [types, setTypes] = useState<Map<string, EquipmentType[]>>(new Map());
  const [availableTypes, setAvailableTypes] =
    useState<EquipmentType[]>(equipmentTypes);
  // This array is for the temporary state of the menu before user hits apply
  const [currentlySelectedFilters, setCurrentlySelectedFilters] = useState<
    number[]
  >([]);
  const [maxExpectedFilters, setMaxExpectedFilters] = useState<
    number | undefined
  >(undefined);

  const prevAvailableTypes = usePrevious(availableTypes);
  const prevEquipmentTypes = usePrevious(equipmentTypes);
  const prevRelevantItemTypes = usePrevious(relevantItemTypeIds);
  const prevEquipmentTypeIds = usePrevious(equipmentTypeIds);

  /** FUNCTION DEFINITIONS START HERE */
  const close = (forceClose = false) => {
    if (isInDropdown) {
      if (forceClose) {
        // workaround: close the outer dropdown by simulating a mousedown event outside it
        const mouseEvent = document.createEvent('MouseEvents');
        mouseEvent.initEvent('mousedown', true, true);
        document.body.dispatchEvent(mouseEvent);
      }
      return;
    }
    document.removeEventListener('keydown', handleKeyPress);
    setOpen(false);
  };

  const handleMenuFilterButtonClick = () => {
    if (!open) {
      document.addEventListener('keydown', handleKeyPress);
    }
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMenuFilterButtonSubmitClick = () => {
    close(true);
    setEquipmentFilter(currentlySelectedFilters);
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.keyCode === 27) {
      close();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef &&
      wrapperRef.current &&
      // https://stackoverflow.com/a/43851475/ cites TS issue tracker
      // casting to Node seems to be the solution
      wrapperRef.current.contains(event.target as Node) === false
    ) {
      close();
    }
  };
  /** FUNCTION DEFINITIONS END HERE */

  /** REACT EFFECTS START HERE */
  const initializeFiltersCB = useCallback(() => {
    initializeFilters(
      relevantItemTypeIds,
      equipmentTypes,
      setAvailableTypes,
      () => groupEquipmentTypeByGroup(availableTypes, setGroups, setTypes)
    );
  }, [
    relevantItemTypeIds,
    equipmentTypes,
    setAvailableTypes,
    availableTypes,
    setGroups,
    setTypes,
  ]);

  useEffect(() => {
    initializeFiltersCB();
    document.addEventListener('mousedown', handleClickOutside);
    isInDropdown &&
      equipmentTypeFilters?.length > 0 &&
      resetIntermediateState(
        equipmentTypeFilters,
        setCurrentlySelectedFilters,
        availableTypes
      );
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process available item types
  useEffect(() => {
    if (
      !equals(prevEquipmentTypes, equipmentTypes) ||
      ((prevRelevantItemTypes === undefined ||
        prevRelevantItemTypes?.length === 0) &&
        relevantItemTypeIds !== undefined &&
        relevantItemTypeIds?.length > 0)
    ) {
      initializeFiltersCB();
    }
  }, [
    equipmentTypes,
    relevantItemTypeIds,
    prevEquipmentTypes,
    prevRelevantItemTypes,
    initializeFiltersCB,
  ]);

  // Set maxExpectedFilters
  useEffect(() => {
    if (
      !equals(prevRelevantItemTypes, relevantItemTypeIds) ||
      !equals(prevEquipmentTypeIds, equipmentTypeIds)
    ) {
      setMaxExpectedFilters(
        relevantItemTypeIds
          ? relevantItemTypeIds?.length
          : equipmentTypeIds?.length
      );
    }
  }, [
    prevRelevantItemTypes,
    relevantItemTypeIds,
    prevEquipmentTypeIds,
    equipmentTypeIds,
  ]);

  // Process items into groups based on the available types
  useEffect(() => {
    if (
      filtersHaveUpdated(
        prevAvailableTypes?.map(({ id }) => id),
        availableTypes.map(({ id }) => id)
      )
    ) {
      groupEquipmentTypeByGroup(availableTypes, setGroups, setTypes);
    }
  }, [prevAvailableTypes, availableTypes]);

  useEffect(() => {
    setCurrentlySelectedFilters(equipmentTypeFilters);
  }, [equipmentTypeFilters]);
  /** REACT EFFECTS END HERE */

  /** DEPENDENT VARIABLES START HERE */
  const selectedAll = currentlySelectedFilters?.length === maxExpectedFilters;
  const areAnySelected = currentlySelectedFilters?.length !== 0;
  const isActive = currentlySelectedFilters?.length > 0 && !selectedAll;
  /** DEPENDENT VARIABLES END HERE */

  const toggleSelectAll = () => {
    let newFilters: number[] = [];
    if (!areAnySelected) {
      newFilters = relevantItemTypeIds || equipmentTypeIds;
    }
    setCurrentlySelectedFilters(newFilters);
  };

  const equipmentTypesByGroup =
    relevantItemGroupId !== undefined
      ? types
          .get(relevantItemGroupId.toString())
          ?.sort(sortByTitleWithEmptyStringToLast)
      : undefined;
  const equipmentTypeToTitleMap = new Map(
    allEquipmentTypes.map((type) => [type.id, type.title])
  );

  const selectedEquipmentTypes = [
    ...new Set(
      allEquipmentTypes
        .filter((equipmentType) => {
          return equipmentTypeFilters.includes(equipmentType.id);
        })
        .map((equipmentType) => equipmentType.id)
    ),
  ];

  const selectedEquipmentGroups = [
    ...new Set(
      equipmentTypeFilters
        .map(
          (equipmentTypeId) =>
            availableTypes.find(
              (type) => Number(equipmentTypeId) === Number(type.id)
            )?.groupId || ''
        )
        .filter((id) => id)
    ),
  ];

  return equipmentTypesByGroup !== undefined ? (
    <GenericFilter
      title="Equipment Type"
      setFilter={setEquipmentFilter}
      items={equipmentTypesByGroup.map((type) => type.id)}
      selectedItems={equipmentTypeFilters}
      transformItemName={(typeId) => {
        const type = equipmentTypesById[typeId];
        return type !== undefined ? type.title : '';
      }}
    />
  ) : (
    <div className={classNames(className, styles.wrapper)} ref={wrapperRef}>
      {!isInDropdown && (
        <Button
          className={classNames(
            styles.button,
            equipmentTypeFilters?.length > 0
              ? styles.dropdownButtonWithSelectedItems
              : '',
            isActive ? styles.buttonActive : '',
            open ? styles.buttonOpen : ''
          )}
          roundedCorners
          variant="outlined"
          onClick={handleMenuFilterButtonClick}
          data-testid={'eqipment-menu-filter-button'}
          disabled={disabled}
        >
          {/*
           * Use equipment group IDs as selected items of FilterLabels, NOT individual equipment type IDs.
           * There are 2 scanarios that an equipment group name appears in the filter label list:
           * 1) an equipment group is selected.
           * 2) at least one equipment type in an equipment group is selected.
           *
           * When user clicks delete button of a filter label, all equipment types of that group is deselected.
           */}
          <FilterLabels
            customTextMaxLength={isOnlyHvacType ? 50 : 0}
            maxItemCount={isOnlyHvacType ? 3 : 2}
            selectedItems={
              isOnlyHvacType ? selectedEquipmentTypes : selectedEquipmentGroups
            }
            title="Equipment Type"
            transformItemName={(groupId) =>
              isOnlyHvacType
                ? equipmentTypeToTitleMap.get(Number(groupId)) || ''
                : groups.get(groupId.toString())?.title || ''
            }
            removeFilter={(groupId) =>
              isOnlyHvacType
                ? handleTypeItemsClick(
                    Number(groupId),
                    currentlySelectedFilters,
                    setCurrentlySelectedFilters,
                    setEquipmentFilter
                  )
                : handleGroupItemsClick(
                    types.get(groupId.toString())?.map((type) => type.id) || [],
                    currentlySelectedFilters,
                    setCurrentlySelectedFilters,
                    setEquipmentFilter
                  )
            }
          />
          <MdExpandMore
            size={16}
            className={open ? styles.openArrowIcon : styles.arrowIcon}
          />
        </Button>
      )}

      {(open || isInDropdown) && (
        <div
          className={classNames(
            isInDropdown
              ? styles.isInCustomDropdownWrapper
              : isOnlyHvacType
              ? styles.dropDownWrapperOrgHvac
              : styles.dropDownWrapper,
            dropDownWrapperClassName
          )}
        >
          <FilterMenuContent
            className={isInDropdown ? styles.selectAllInCustomDropdown : ''}
          >
            <SelectAll
              areAnySelected={areAnySelected}
              toggleSelectAll={toggleSelectAll}
            />
          </FilterMenuContent>
          {relevantItemGroupId === undefined && (
            <FilterMenuContent
              className={isInDropdown ? styles.listInCustomDropdown : ''}
            >
              <ul className={styles.listWrapper}>
                {Array.from(groups.values())
                  .sort(sortByTitleWithEmptyStringToLast)
                  .map((group) => {
                    const equipmentTypesByGroup = types
                      .get(group.id.toString())
                      ?.sort(sortByTitleWithEmptyStringToLast);

                    if (
                      (relevantItemGroupId !== undefined &&
                        group.id !== relevantItemGroupId) ||
                      !equipmentTypesByGroup
                    ) {
                      return null;
                    }

                    const equipmentTypeIds = equipmentTypesByGroup.map(
                      (equipmentType) => equipmentType.id
                    );

                    const activeItems = equipmentTypeIds.reduce(
                      (state, id) =>
                        currentlySelectedFilters.includes(id)
                          ? state + 1
                          : state,
                      0
                    );

                    return (
                      <>
                        {!isOnlyHvacType ? (
                          <EquipmentMenuItem
                            alias={group.alias}
                            key={`EquipmentMenuItem${group.id}`}
                            isCompact={isInDropdown}
                            title={group.title}
                            onClick={() =>
                              handleGroupItemsClick(
                                equipmentTypeIds,
                                currentlySelectedFilters,
                                setCurrentlySelectedFilters
                              )
                            }
                            activeItems={activeItems}
                            maxItems={equipmentTypeIds?.length}
                          >
                            {equipmentTypesByGroup.map((equipmentType) => (
                              <EquipmentMenuListItem
                                key={`EquipmentMenuListItem${equipmentType.id}`}
                                isCompact={isInDropdown}
                                onClick={() =>
                                  handleTypeItemsClick(
                                    equipmentType.id,
                                    currentlySelectedFilters,
                                    setCurrentlySelectedFilters
                                  )
                                }
                                label={equipmentType.title}
                                checked={currentlySelectedFilters.includes(
                                  equipmentType.id
                                )}
                              />
                            ))}
                          </EquipmentMenuItem>
                        ) : (
                          <>
                            {equipmentTypesByGroup.map((equipmentType) => (
                              <EquipmentMenuListItem
                                isOnlyHvacType={true}
                                key={`EquipmentMenuListItem${equipmentType.id}`}
                                isCompact={isInDropdown}
                                onClick={() =>
                                  handleTypeItemsClick(
                                    equipmentType.id,
                                    currentlySelectedFilters,
                                    setCurrentlySelectedFilters
                                  )
                                }
                                label={equipmentType.title}
                                checked={currentlySelectedFilters.includes(
                                  equipmentType.id
                                )}
                                variant={
                                  isOnlyHvacType ? 'contained' : 'outlined'
                                }
                              />
                            ))}
                          </>
                        )}
                      </>
                    );
                  })}
              </ul>
            </FilterMenuContent>
          )}
          <FilterMenuFooter
            className={isInDropdown ? styles.footerInCustomDropdown : ''}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => {
                resetIntermediateState(
                  equipmentTypeFilters,
                  setCurrentlySelectedFilters,
                  availableTypes
                );
                close(true);
              }}
            >
              Cancel
            </Button>

            <Button size="small" onClick={handleMenuFilterButtonSubmitClick}>
              Apply
            </Button>
          </FilterMenuFooter>
        </div>
      )}
    </div>
  );
};

const initializeFilters = (
  relevantItemTypeIds: number[] | undefined,
  equipmentTypes: EquipmentType[],
  setAvailableTypes: React.Dispatch<React.SetStateAction<EquipmentType[]>>,
  groupEquipmentTypeByGroup: () => void
) => {
  if (relevantItemTypeIds !== undefined && relevantItemTypeIds?.length > 0) {
    const filtered = equipmentTypes.filter(({ id }) => {
      return relevantItemTypeIds.includes(id);
    });
    setAvailableTypes(filtered);
  } else {
    groupEquipmentTypeByGroup();
  }
};

const groupEquipmentTypeByGroup = (
  availableTypes: EquipmentType[],
  setGroups: React.Dispatch<React.SetStateAction<Map<string, EquipmentGroup>>>,
  setTypes: React.Dispatch<React.SetStateAction<Map<string, EquipmentType[]>>>
) => {
  const groups: Map<string, EquipmentGroup> = new Map();
  const types: Map<string, EquipmentType[]> = new Map();

  for (const equipmentType of availableTypes) {
    const groupId = equipmentType.groupId.toString();
    const existingItems = types.get(groupId) || [];
    types.set(groupId, existingItems.concat(equipmentType));
    if (equipmentType.group) {
      groups.set(groupId, equipmentType.group);
    }
  }

  setGroups(groups);
  setTypes(types);
};

const handleGroupItemsClick = (
  equipmentTypeIds: number[],
  currentlySelectedFilters: number[],
  setCurrentlySelectedFilters: React.Dispatch<React.SetStateAction<number[]>>,
  setEquipmentFilter = (types) => {}
) => {
  const newActiveFilter = currentlySelectedFilters.some((i) =>
    equipmentTypeIds.some((filterItem) => i === filterItem)
  )
    ? currentlySelectedFilters.filter(
        (item) => equipmentTypeIds.includes(item) === false
      )
    : [...currentlySelectedFilters, ...equipmentTypeIds];
  setCurrentlySelectedFilters(newActiveFilter);

  setEquipmentFilter?.(newActiveFilter);
};

const handleTypeItemsClick = (
  typeId: number,
  currentlySelectedFilters: number[],
  setCurrentlySelectedFilters: React.Dispatch<React.SetStateAction<number[]>>,
  setEquipmentFilter = (types) => {}
) => {
  const newActiveFilter = currentlySelectedFilters.includes(typeId)
    ? currentlySelectedFilters.filter((f) => f !== typeId)
    : [...currentlySelectedFilters, typeId];
  setCurrentlySelectedFilters(newActiveFilter);
  setEquipmentFilter?.(newActiveFilter);
};

const filtersHaveUpdated = (prev: number[] | undefined, next: number[]) => {
  if (prev === undefined) return true;
  return JSON.stringify(prev.sort()) !== JSON.stringify(next.sort());
};

const resetIntermediateState = (
  activeFilters: number[],
  setCurrentlySelectedFilters: React.Dispatch<React.SetStateAction<number[]>>,
  availableTypes: EquipmentType[]
) => {
  if (activeFilters?.length === 0) {
    setCurrentlySelectedFilters(availableTypes.map(({ id }) => id));
  } else {
    setCurrentlySelectedFilters(activeFilters);
  }
};

export default EquipmentMenuFilter;
